import { useBoolean } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import iconFilter from '~/assets/svgs/icon-filter.svg?react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { FilterModal } from '~/components/shared/FilterModal'
import { MangoButton } from '~/components/shared/MangoButton'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { FilterSection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

interface FilterButtonProps {
  filterData?: FilterSection[]
  title?: string
  activeFiltersCount?: number
  onFilterChangesApplied?: (updatedFilters: FilterSection[]) => void
  onFilterModalClosed?: () => void
  useQuickActionButton?: boolean
}

export const FilterButton = ({
  filterData = [],
  title = 'Filters',
  activeFiltersCount = 0,
  onFilterChangesApplied,
  onFilterModalClosed,
  useQuickActionButton,
}: FilterButtonProps) => {
  const [modalOpen, setModalOpen] = useBoolean(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleHoverStateChange = (isHovered: boolean) => {
    setIsHovered(isHovered)
  }

  const handleButtonClick = () => {
    setModalOpen.on()
  }

  const handleModalClose = () => {
    setModalOpen.off()
    onFilterModalClosed?.()
  }

  const handleFiltersApplied = (updatedFilterData: FilterSection[]) => {
    onFilterChangesApplied?.(updatedFilterData)
    handleModalClose()
  }

  const badgeVariant = useMemo(() => {
    return isHovered
      ? NotificationBadgeVariant.FilterHover
      : NotificationBadgeVariant.Info
  }, [isHovered])

  const badgeNode = useMemo(() => {
    if (activeFiltersCount === 0) {
      return null
    }
    return (
      <NotificationBadge
        size={Size.sm}
        variant={badgeVariant}
        count={activeFiltersCount}
      />
    )
  }, [activeFiltersCount, badgeVariant])

  const tooltipText = useMemo(() => {
    if (activeFiltersCount === 0) return title
    if (activeFiltersCount === 1)
      return `${title} (${activeFiltersCount} filter applied)`
    return `${title} (${activeFiltersCount} filters applied)`
  }, [title, activeFiltersCount])

  return (
    <>
      {useQuickActionButton ? (
        <QuickActionButton
          actionIcon={iconFilter}
          tooltipText={tooltipText}
          tooltipDisabled={modalOpen}
          variant={
            activeFiltersCount > 0
              ? QuickActionButtonVariants.Active_200
              : QuickActionButtonVariants.Default_200
          }
          onClick={handleButtonClick}
        />
      ) : (
        <MangoButton
          variant={MangoButtonVariant.Default_200}
          leftIcon={iconFilter}
          onClick={handleButtonClick}
          rightNode={badgeNode}
          onHoverStateChange={handleHoverStateChange}
        >
          {title}
        </MangoButton>
      )}
      <FilterModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        filterSections={filterData}
        onFiltersApplied={handleFiltersApplied}
      />
    </>
  )
}
